import mParticle from '@mparticle/web-sdk';
import type { SDKInitConfig, IUserIdentities } from '@mparticle/web-sdk';
import * as Sentry from '@sentry/react';

import type { UserType } from 'types/user';

const ENABLED_DEPLOY_ENVS = ['prod', 'qa', 'stg', 'uat'];

let isReady = false;
let lastIdentifiedUserId: string | null = null;

function isEnvEnabled(): boolean {
  return ENABLED_DEPLOY_ENVS.includes(import.meta.env.VITE_DEPLOY_ENV);
}

export function initializeMParticle(): void {
  if (isReady) return;
  if (!isEnvEnabled()) return;

  const apiKey = import.meta.env.VITE_MPARTICLE_API_KEY;
  if (!apiKey) return;

  const config: SDKInitConfig = {
    isDevelopmentMode: import.meta.env.VITE_DEPLOY_ENV !== 'prod',
    identifyRequest: { userIdentities: {} as IUserIdentities },
  };

  mParticle.init(apiKey, config);
  isReady = true;
}

export function identifyUser(user: UserType): void {
  if (!isReady) return;
  if (!user?.id) return;
  if (lastIdentifiedUserId === user.id) return;

  const userIdentities: IUserIdentities = {
    customerid: user.id,
    email: user.email_address,
  };

  mParticle.Identity.login?.({ userIdentities }, result => {
    const mpUser = result?.getUser?.();
    if (!mpUser) return;

    mpUser.setUserAttribute('$FirstName', user.first_name);
    mpUser.setUserAttribute('$LastName', user.last_name);
    mpUser.setUserAttribute('group_id', user.group_id);
    mpUser.setUserAttribute('company_id', user.company_id);
    mpUser.setUserAttribute('is_active', user.is_active);
    mpUser.setUserAttribute('web_app_version', import.meta.env.VITE_APP_VERSION);

    // Mark as identified only after the SDK successfully returned a user.
    // If the network call failed (mpUser is null above), we leave the flag
    // unset so the next render can retry.
    lastIdentifiedUserId = user.id;
  });
}

export function logoutUser(): void {
  if (!isReady) return;
  if (!lastIdentifiedUserId) return;

  mParticle.Identity.logout?.({});
  lastIdentifiedUserId = null;
}

export type SharedEventAttributes = {
  group_id?: string | null;
  restaurant_brand_id?: string | null;
  restaurant_location_id?: string | null;
  /** Cuboh-only merchants have no CN brand/location, so this is their only identity. */
  cuboh_company_id?: string | number | null;
};

// Module-level store for the shared attributes that must ride along on every event's
// custom_attributes (per the data plan's Shared Event Attributes sheet). This is plain
// in-memory state — no SDK readiness needed to set it — and is merged into each logEvent /
// logPageView payload below.
let sharedEventAttributes: SharedEventAttributes = {};

export function setSharedEventAttributes(attrs: SharedEventAttributes): void {
  const next: SharedEventAttributes = {};
  (Object.keys(attrs) as Array<keyof SharedEventAttributes>).forEach(key => {
    const value = attrs[key];
    // Drop empty strings too: during a cold page load the context can briefly emit
    // '' before it resolves, and we never want a half-loaded sync to write a
    // misleading empty value onto every event.
    if (value === null || value === undefined || value === '') return;
    next[key] = String(value);
  });
  sharedEventAttributes = next;
}

export function getSharedEventAttributes(): SharedEventAttributes {
  return { ...sharedEventAttributes };
}

export type AnalyticsAttributeValue = string | number | boolean | null | undefined;
export type AnalyticsAttributes = Record<string, AnalyticsAttributeValue>;

// Drops null/undefined keys; preserves string/number/boolean values as-is so that
// numeric attributes reach mParticle as true numbers (per the menu editor data plan).
function sanitizeAttributes(attrs: AnalyticsAttributes): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  Object.keys(attrs).forEach(key => {
    const value = attrs[key];
    if (value === null || value === undefined) return;
    out[key] = value;
  });
  return out;
}

// No-op-safe custom event logger. Inherits the prod/stg-only `isReady` guard, so it
// does nothing in dev/local or when the SDK failed to initialize.
export function logEvent(eventName: string, attributes: AnalyticsAttributes = {}): void {
  if (!isReady) return;
  if (!eventName) return;

  // Analytics is best-effort and must never break the app flow it's instrumented into
  // (these calls live on success paths inside try/catch blocks across the menu editor).
  try {
    mParticle.logEvent(
      eventName,
      mParticle.EventType.Other,
      sanitizeAttributes({ ...getSharedEventAttributes(), ...attributes }),
    );
  } catch (error) {
    Sentry.captureException(error);
  }
}

export function logPageView(pageName: string, attributes: AnalyticsAttributes = {}): void {
  if (!isReady || !pageName) return;
  try {
    const attrs = sanitizeAttributes({ ...getSharedEventAttributes(), ...attributes });
    mParticle.logPageView(pageName, attrs);
  } catch (error) {
    Sentry.captureException(error);
  }
}

export const testingInternals = {
  reset(): void {
    isReady = false;
    lastIdentifiedUserId = null;
    sharedEventAttributes = {};
  },
  isReady: (): boolean => isReady,
};
